/**
 * Inbox → match an existing bill or expense: the wire contract.
 *
 *  - `POST /api/v1/inbox-matches/orgs/:orgId/candidates` — body
 *    {@link inboxMatchCandidatesRequestSchema}, response
 *    {@link InboxMatchCandidatesResponse}. The scoring itself is the pure
 *    library in `../documentMatch/` (one rule, one place); this file only
 *    describes what crosses the wire.
 *  - `PATCH /api/v1/ap-invoices/orgs/:orgId/:id/recurring-invoice-number` —
 *    body {@link apRecurringInvoiceNumberSchema}; a compare-and-swap that
 *    replaces an untouched recurring placeholder number on a pending bill.
 *    Refusals are 409s carrying one of {@link INBOX_MATCH_ERROR_CODES}.
 *
 * Reason codes are machine codes, never English — the web renders them
 * through `inbox.match.*` i18n.
 */
import { z } from "zod";
/** Most messages one candidates request may score. */
export const INBOX_MATCH_MAX_MESSAGES = 100;
/** Candidates kept per message (after ranking). */
export const INBOX_MATCH_TOP_N = 3;
export const matchCandidateKindEnum = z.enum(["ap_bill", "expense"]);
export const matchStrengthEnum = z.enum(["strong", "possible"]);
/** Display status of the candidate record (plain text in the grid). */
export const matchCandidateStatusEnum = z.enum(["pending", "on_hold", "approved", "partially_paid", "paid"]);
/**
 * Why a candidate scored as it did. One code per signal outcome; the grid's
 * Why column renders them in this order.
 */
export const matchReasonEnum = z.enum([
    /** Normalised invoice number equals the bill's number / expense reference. */
    "number_match",
    /** The record carries a different, real (non-placeholder) number. */
    "number_conflict",
    "vendor_match",
    /** Both names present and neither equals nor contains the other. */
    "vendor_mismatch",
    "amount_exact",
    /** Within 10% but not to the cent. */
    "amount_near",
    /** Comparable, but more than 10% apart. */
    "amount_differs",
    /** Not compared: the document or the record is not in the org currency. */
    "amount_skipped_currency",
    /** Document date within ±20 days of the record date. */
    "date_within",
    "date_outside",
    /** The record already holds an invoice/receipt or a real vendor number. */
    "already_documented",
]);
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * Document vs record differences — shown, never applied. Every `*Days` is
 * document minus record in calendar days; `amount` is document minus record
 * in major units, present only when both sides are in the same currency.
 */
export const matchDiffsSchema = z.object({
    /** The vendor / merchant name as parsed from the document. */
    documentVendor: z.string().nullable(),
    /** The invoice number as printed on the document. */
    documentNumber: z.string().nullable(),
    /** The number compared with it: the bill's invoice number, the expense's reference. */
    recordNumber: z.string().nullable(),
    documentAmount: z.number().nullable(),
    documentCurrency: z.string().nullable(),
    recordAmount: z.number(),
    recordCurrency: z.string().nullable(),
    /** Signed difference (document − record); null when not comparable. */
    amount: z.number().nullable(),
    documentDate: isoDay.nullable(),
    /** null when the document carries no ISO date. */
    dateDays: z.number().int().nullable(),
    documentDueDate: isoDay.nullable(),
    dueDateDays: z.number().int().nullable(),
});
/**
 * The one automatic change a match may make: the bill's recurring
 * placeholder number replaced with the vendor's. `invoiceNumberBlocked` is
 * the "carries the placeholder but was not replaced" note (approved bill).
 */
export const matchFillsSchema = z.object({
    invoiceNumber: z
        .object({ from: z.string(), to: z.string() })
        .optional(),
    invoiceNumberBlocked: z
        .object({ current: z.string(), reason: z.literal("not_pending") })
        .optional(),
});
export const rankedCandidateSchema = z.object({
    kind: matchCandidateKindEnum,
    id: z.string(),
    /** Display number: the bill's invoice number, the expense's EXP- number. */
    number: z.string(),
    payeeName: z.string().nullable(),
    date: isoDay,
    dueDate: isoDay.nullable(),
    status: matchCandidateStatusEnum,
    amount: z.number(),
    currency: z.string().nullable(),
    strength: matchStrengthEnum,
    reasons: z.array(matchReasonEnum),
    alreadyDocumented: z.boolean(),
    diffs: matchDiffsSchema,
    fills: matchFillsSchema,
    /** The attachment whose parsed data produced this (best) score. */
    attachmentId: z.string().nullable(),
    /**
     * The record this (processed) message is already linked to — the detail
     * shows it as the match, with no Match action. Absent on suggestions.
     */
    linked: z.boolean().optional(),
});
/** "INV-8812 is already booked as BILL-x" — also shown on the Create path. */
export const duplicateBookingSchema = z.object({
    /** The parsed number, as printed on the document. */
    invoiceNumber: z.string(),
    kind: matchCandidateKindEnum,
    id: z.string(),
    number: z.string(),
});
export const inboxMatchCandidatesRequestSchema = z
    .object({
    messageIds: z
        .array(z.string().uuid())
        .min(1)
        .max(INBOX_MATCH_MAX_MESSAGES),
})
    .strict();
export const inboxMatchCandidatesResponseSchema = z.object({
    /** messageId → up to {@link INBOX_MATCH_TOP_N} candidates, best first. */
    candidates: z.record(z.string(), z.array(rankedCandidateSchema)),
    /** messageId → bookings that already carry a parsed number. */
    duplicates: z.record(z.string(), z.array(duplicateBookingSchema)),
    /** A candidate window hit the row cap; results may be incomplete. */
    capped: z.boolean(),
});
/** The longest invoice number the recurring-number PATCH accepts (and the matcher offers). */
export const MAX_INVOICE_NUMBER_LENGTH = 100;
/** PATCH …/ap-invoices/orgs/:orgId/:id/recurring-invoice-number */
export const apRecurringInvoiceNumberSchema = z
    .object({ invoice_number: z.string().trim().min(1).max(MAX_INVOICE_NUMBER_LENGTH) })
    .strict();
/** 409 codes (the web reads `err.envelope.code`; i18n `inbox.match.errors.<code>`). */
export const INBOX_MATCH_ERROR_CODES = {
    /** Zero rows swapped: the number was edited, or the bill is no longer pending. */
    notPlaceholder: "not_placeholder",
    /** The number is already on another bill of the same vendor. */
    duplicateInvoiceNumber: "duplicate_invoice_number",
};
//# sourceMappingURL=inboxMatches.js.map